import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  Video,
  ShieldCheck,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  ArrowRight,
  UserCheck,
  Clock,
  ExternalLink,
  Film,
} from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import api from '@/lib/api';

import { TwinneeLogo } from '@/components/TwinneeLogo';
/**
 * DigitalTwinPage — 3-screen flow for creating a Digital Twin via HeyGen.
 *
 * Screen 1: Upload     — pick file → upload to S3 via pre-signed URL → confirm-upload → redirect to consent
 * Screen 2: Waiting    — shown at /avatar/callback after consent; polls status every 10s
 * Screen 3: Ready      — avatar_id is available; show "Generate Video" button
 *
 * Edge-cases (Section 8 of the plan):
 *   - Tab closed during consent → on mount, resume from localStorage avatar_project_id
 *   - consent_status == "rejected" → show retry screen with fresh consent_url
 *   - Polling > 15 min → show "taking longer than usual" banner
 *   - S3 upload failure → retry with same avatar_project_id and s3_key
 */

const POLL_INTERVAL_MS = 10_000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

// ─── Animated step indicator ────────────────────────────────────────────
const STEPS = [
  { key: 'uploaded', label: 'Uploaded', icon: Upload },
  { key: 'verified', label: 'Consent Verified', icon: ShieldCheck },
  { key: 'building', label: 'Building Twin', icon: TwinneeLogo },
  { key: 'ready', label: 'Ready', icon: CheckCircle2 },
];

function getActiveStepIndex(avatarStatus, consentStatus) {
  if (avatarStatus === 'ready') return 3;
  if (avatarStatus === 'processing') return 2;
  if (['approved', 'accepted', 'completed', 'complete', 'verified', 'verified_consent', 'active'].includes((consentStatus || '').toLowerCase())) return 2;
  if (avatarStatus === 'waiting_for_consent') return 1;
  return 0;
}

const StepIndicator = ({ activeIndex }) => (
  <div className="flex items-center justify-center gap-2 sm:gap-3 w-full py-6">
    {STEPS.map((step, i) => {
      const Icon = step.icon;
      const isDone = i < activeIndex;
      const isCurrent = i === activeIndex;
      return (
        <div key={step.key} className="flex items-center gap-2">
          <motion.div
            className={`
              flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium
              transition-all duration-300
              ${isDone
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : isCurrent
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40 shadow-lg shadow-purple-500/10'
                  : 'bg-white/5 text-white/30 border border-white/10'
              }
            `}
            animate={isCurrent ? { scale: [1, 1.05, 1] } : {}}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            {isDone ? (
              <CheckCircle2 className="w-3.5 h-3.5" />
            ) : isCurrent ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Icon className="w-3.5 h-3.5" />
            )}
            <span className="hidden sm:inline">{step.label}</span>
          </motion.div>
          {i < STEPS.length - 1 && (
            <div className={`w-6 h-px ${i < activeIndex ? 'bg-emerald-500/40' : 'bg-white/10'}`} />
          )}
        </div>
      );
    })}
  </div>
);

// ─── Main Page Component ────────────────────────────────────────────────
export const DigitalTwinPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Screen: 'upload' | 'waiting' | 'ready' | 'failed'
  const [screen, setScreen] = useState('upload');

  // Upload state
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [avatarName, setAvatarName] = useState('My Digital Twin');
  const fileInputRef = useRef(null);

  // Project tracking
  const [avatarProjectId, setAvatarProjectId] = useState('');
  const [s3Key, setS3Key] = useState('');
  const [groupId, setGroupId] = useState('');
  const [avatarId, setAvatarId] = useState('');
  const [consentUrl, setConsentUrl] = useState('');
  const [consentStatus, setConsentStatus] = useState('');
  const [avatarStatus, setAvatarStatus] = useState('');
  const [error, setError] = useState(null);

  // Polling state
  const pollRef = useRef(null);
  const pollStartRef = useRef(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);

  // ─── On mount: check for in-progress project ─────────────────────
  useEffect(() => {
    const isCallback = location.pathname.includes('/avatar/callback') || location.pathname.includes('/avatar/success');
    const savedProjectId = localStorage.getItem('dt_avatar_project_id');
    const avatarCastSession = localStorage.getItem('twinnee_avatar_cast_session');

    if (avatarCastSession) {
      // User created digital twin in AvatarCast Studio — redirect back to resume session
      navigate('/avatar-cast', { replace: true });
      return;
    }

    if (savedProjectId) {
      setAvatarProjectId(savedProjectId);
      // If we're on the callback page OR we have a saved project, check its status
      checkAndResumeProject(savedProjectId);
    } else if (isCallback) {
      // Got redirected back from HeyGen but no saved project? Show error
      setError('No avatar project found. Please start the process again.');
      setScreen('upload');
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Check project status and resume ─────────────────────────────
  const checkAndResumeProject = useCallback(async (projectId) => {
    try {
      const res = await api.get(`/api/avatar/status/${projectId}`);
      const data = res.data;
      setConsentStatus(data.consent_status || '');
      setAvatarStatus(data.avatar_status || '');
      setAvatarId(data.avatar_id || '');

      if (data.avatar_status === 'ready') {
        setScreen('ready');
      } else if (data.avatar_status === 'failed' && ['rejected', 'fail'].includes(data.consent_status)) {
        // Consent was rejected — show retry
        setConsentUrl(data.consent_url || '');
        setScreen('waiting');
      } else if (['waiting_for_consent', 'processing'].includes(data.avatar_status)) {
        setScreen('waiting');
        startPolling(projectId);
      } else {
        // uploading or other early state — show upload screen
        setScreen('upload');
      }
    } catch (err) {
      console.error('Failed to check project status:', err);
      // Project may not exist anymore, clear localStorage
      localStorage.removeItem('dt_avatar_project_id');
      setScreen('upload');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Polling ─────────────────────────────────────────────────────
  const startPolling = useCallback((projectId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollStartRef.current = Date.now();
    setPollTimedOut(false);

    const poll = async () => {
      try {
        const res = await api.get(`/api/avatar/status/${projectId}`);
        const data = res.data;
        setConsentStatus(data.consent_status || '');
        setAvatarStatus(data.avatar_status || '');
        setAvatarId(data.avatar_id || '');

        if (data.avatar_status === 'ready') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setScreen('ready');
          localStorage.removeItem('dt_avatar_project_id');
          toast.success('Your Digital Twin is ready!');
        } else if (data.avatar_status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (['rejected', 'fail'].includes(data.consent_status)) {
            setConsentUrl(data.consent_url || '');
            // Stay on waiting screen to show retry UI
          } else {
            setError('Avatar creation failed. Please try again.');
            setScreen('failed');
          }
        }

        // Check timeout
        if (pollStartRef.current && (Date.now() - pollStartRef.current) > POLL_TIMEOUT_MS) {
          setPollTimedOut(true);
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    };

    poll(); // immediate first check
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
  }, []);

  // ─── Upload Flow ─────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp4|webm|mov|avi|mkv)$/i)) {
      toast.error('Please select a valid video file (MP4, WebM, MOV, AVI, or MKV)');
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      toast.error('Video must be under 500MB');
      return;
    }
    setVideoFile(file);
    setError(null);
  };

  const handleUploadAndCreate = async () => {
    if (!videoFile) {
      toast.error('Please select a video file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Step 1: Get pre-signed S3 PUT URL
      toast.info('Preparing upload...');
      const urlRes = await api.post('/api/avatar/upload-url', {
        filename: videoFile.name,
        content_type: videoFile.type || 'video/mp4',
      });
      const { uploadUrl, key, avatar_project_id } = urlRes.data;
      setAvatarProjectId(avatar_project_id);
      setS3Key(key);
      localStorage.setItem('dt_avatar_project_id', avatar_project_id);

      // Step 2: Upload directly to S3 via pre-signed PUT
      toast.info('Uploading video to cloud...');
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', videoFile.type || 'video/mp4');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`S3 upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during S3 upload'));
        xhr.send(videoFile);
      });

      setUploadProgress(100);
      toast.success('Video uploaded!');

      // Step 3: Confirm upload → create HeyGen Digital Twin → get consent URL
      toast.info('Creating your Digital Twin...');
      const confirmRes = await api.post('/api/avatar/confirm-upload', {
        avatar_project_id,
        s3_key: key,
        name: avatarName,
      });

      const {
        consent_url,
        group_id: gId,
        avatar_id: aId,
        consent_status: cStatus,
      } = confirmRes.data;

      setGroupId(gId || '');
      setAvatarId(aId || '');
      setConsentStatus(cStatus || '');
      setConsentUrl(consent_url || '');

      if (consent_url) {
        // Step 4: Redirect to HeyGen consent page
        toast.info('Redirecting to consent verification...');
        window.location.href = consent_url;
      } else {
        // Consent might already be approved or not required — go to waiting
        setScreen('waiting');
        startPolling(avatar_project_id);
      }
    } catch (err) {
      console.error('Upload/create error:', err);
      const msg = err.response?.data?.detail || err.message || 'Something went wrong';
      setError(msg);
      toast.error(msg);
      setUploading(false);
    }
  };

  // ─── Retry upload (re-use same avatar_project_id) ────────────────
  const handleRetryUpload = () => {
    setError(null);
    setUploading(false);
    setUploadProgress(0);
    // Keep videoFile and avatarProjectId for re-use (Section 8: S3 upload failure)
    setScreen('upload');
  };

  // ─── Retry consent ──────────────────────────────────────────────
  const handleRetryConsent = () => {
    if (consentUrl) {
      window.location.href = consentUrl;
    } else {
      toast.error('No consent URL available. Please try again.');
    }
  };

  // ─── Navigate to video generation ────────────────────────────────
  const handleGenerateVideo = () => {
    navigate('/avatar-cast', { state: { avatarId, groupId } });
  };

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 pt-24 pb-16">

        {/* Page header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 mb-4">
            <UserCheck className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-purple-300 font-medium">Digital Twin</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-white via-purple-200 to-purple-400 bg-clip-text text-transparent">
            Create Your Digital Twin
          </h1>
          <p className="text-sm text-white/50 mt-2 max-w-md mx-auto">
            Upload a training video, verify your identity, and get a reusable AI avatar.
          </p>
        </motion.div>

        <AnimatePresence mode="wait">

          {/* ═══════════ SCREEN 1: Upload ═══════════ */}
          {screen === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white/[0.03] border-white/10 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Video className="w-5 h-5 text-purple-400" />
                    Upload Training Video
                  </CardTitle>
                  <CardDescription className="text-white/50">
                    Record or upload a 30–120 second video of yourself speaking clearly.
                    This will be used to create your AI twin.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">

                  {/* Avatar name */}
                  <div>
                    <label className="text-sm text-white/60 mb-1.5 block">Twin Name</label>
                    <input
                      type="text"
                      value={avatarName}
                      onChange={(e) => setAvatarName(e.target.value)}
                      placeholder="My Digital Twin"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                    />
                  </div>

                  {/* File drop zone */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`
                      relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
                      transition-all duration-300 group
                      ${videoFile
                        ? 'border-emerald-500/40 bg-emerald-500/5'
                        : 'border-white/15 bg-white/[0.02] hover:border-purple-500/40 hover:bg-purple-500/5'
                      }
                    `}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.avi,.mkv"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="dt-video-file-input"
                    />
                    {videoFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <Film className="w-10 h-10 text-emerald-400" />
                        <p className="text-sm text-emerald-300 font-medium">{videoFile.name}</p>
                        <p className="text-xs text-white/40">
                          {(videoFile.size / (1024 * 1024)).toFixed(1)} MB · Click to change
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="w-10 h-10 text-white/25 group-hover:text-purple-400 transition-colors" />
                        <p className="text-sm text-white/50 group-hover:text-white/70 transition-colors">
                          Click to select a video file
                        </p>
                        <p className="text-xs text-white/30">MP4, WebM, MOV, AVI, MKV · Max 500MB</p>
                      </div>
                    )}
                  </div>

                  {/* Upload progress */}
                  {uploading && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-white/50">
                        <span>Uploading to cloud...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-1.5">
                        <motion.div
                          className="bg-gradient-to-r from-purple-500 to-purple-400 h-1.5 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${uploadProgress}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Error */}
                  {error && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                      <p className="text-sm text-red-300">{error}</p>
                    </div>
                  )}

                  {/* Action button */}
                  <Button
                    onClick={handleUploadAndCreate}
                    disabled={!videoFile || uploading}
                    className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white h-11 rounded-lg disabled:opacity-40"
                    id="dt-start-upload-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {uploadProgress < 100 ? 'Uploading...' : 'Creating Twin...'}
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-4 h-4" />
                        Upload & Create Twin
                      </>
                    )}
                  </Button>

                  {/* Info note */}
                  <p className="text-xs text-white/30 text-center">
                    Your video is uploaded securely via a short-lived pre-signed URL. It never passes through our servers.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* ═══════════ SCREEN 2: Waiting ═══════════ */}
          {screen === 'waiting' && (
            <motion.div
              key="waiting"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white/[0.03] border-white/10 backdrop-blur-sm">
                <CardHeader className="text-center">
                  <CardTitle className="text-white flex items-center justify-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-purple-400" />
                    {avatarStatus === 'failed' && ['rejected', 'fail'].includes(consentStatus)
                      ? 'Consent Rejected'
                      : 'Building Your Twin'
                    }
                  </CardTitle>
                  <CardDescription className="text-white/50">
                    {avatarStatus === 'failed' && ['rejected', 'fail'].includes(consentStatus)
                      ? 'Your consent verification was rejected. You can re-record it below.'
                      : 'Verifying consent and processing your digital twin. This may take a few minutes.'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">

                  {/* Step indicator */}
                  <StepIndicator activeIndex={getActiveStepIndex(avatarStatus, consentStatus)} />

                  {/* Status info */}
                  <div className="flex flex-col items-center gap-3 py-4">
                    {avatarStatus === 'failed' && ['rejected', 'fail'].includes(consentStatus) ? (
                      <>
                        <div className="p-3 rounded-full bg-red-500/10 border border-red-500/20">
                          <AlertCircle className="w-8 h-8 text-red-400" />
                        </div>
                        <p className="text-sm text-red-300 text-center">
                          Consent was rejected by HeyGen. Please re-record your consent video.
                        </p>
                        <Button
                          onClick={handleRetryConsent}
                          disabled={!consentUrl}
                          className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white rounded-lg"
                          id="dt-retry-consent-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Re-record Consent
                          <ExternalLink className="w-3 h-3 ml-1 opacity-50" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="relative">
                          <div className="p-4 rounded-full bg-purple-500/10 border border-purple-500/20">
                            <Loader2 className="w-10 h-10 text-purple-400 animate-spin" />
                          </div>
                          <motion.div
                            className="absolute inset-0 rounded-full border-2 border-purple-500/20"
                            animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                          />
                        </div>
                        <p className="text-sm text-white/50 text-center">
                          {consentStatus === 'pending' || avatarStatus === 'waiting_for_consent'
                            ? 'Waiting for consent approval...'
                            : 'Processing your digital twin...'}
                        </p>
                        <p className="text-xs text-white/30">Checking every 10 seconds</p>
                      </>
                    )}
                  </div>

                  {/* Polling timeout warning */}
                  {pollTimedOut && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <Clock className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm text-amber-300 font-medium">Taking longer than usual</p>
                        <p className="text-xs text-white/40 mt-1">
                          This is normal for complex videos. You can safely close this tab and check back later.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Manual refresh */}
                  <div className="flex justify-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => avatarProjectId && checkAndResumeProject(avatarProjectId)}
                      className="text-white/40 hover:text-white/70 text-xs"
                      id="dt-manual-refresh-btn"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Refresh manually
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* ═══════════ SCREEN 3: Ready ═══════════ */}
          {screen === 'ready' && (
            <motion.div
              key="ready"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.4 }}
            >
              <Card className="bg-white/[0.03] border-white/10 backdrop-blur-sm overflow-hidden">
                {/* Celebration gradient */}
                <div className="h-1 bg-gradient-to-r from-emerald-500 via-purple-500 to-pink-500" />
                <CardHeader className="text-center pt-8">
                  <motion.div
                    className="inline-flex mx-auto p-4 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', delay: 0.2 }}
                  >
                    <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                  </motion.div>
                  <CardTitle className="text-white text-2xl">Your Digital Twin is Ready!</CardTitle>
                  <CardDescription className="text-white/50 mt-1">
                    You can now generate unlimited videos with your AI avatar.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pb-8">

                  {/* Avatar ID reference */}
                  {avatarId && (
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-center">
                      <p className="text-xs text-white/40 mb-1">Avatar ID</p>
                      <p className="text-sm text-white/80 font-mono select-all">{avatarId}</p>
                    </div>
                  )}

                  {/* Step indicator — all done */}
                  <StepIndicator activeIndex={4} />

                  {/* Actions */}
                  <div className="flex flex-col gap-3">
                    <Button
                      onClick={handleGenerateVideo}
                      className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white h-12 rounded-lg text-base"
                      id="dt-generate-video-btn"
                    >
                      <TwinneeLogo className="w-5 h-5" />
                      Generate Video
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setScreen('upload');
                        setVideoFile(null);
                        setAvatarProjectId('');
                        setAvatarId('');
                        localStorage.removeItem('dt_avatar_project_id');
                      }}
                      className="text-white/40 hover:text-white/60 text-sm"
                      id="dt-create-another-btn"
                    >
                      Create another twin
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* ═══════════ SCREEN: Failed ═══════════ */}
          {screen === 'failed' && (
            <motion.div
              key="failed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white/[0.03] border-white/10 backdrop-blur-sm">
                <CardHeader className="text-center">
                  <div className="inline-flex mx-auto p-3 rounded-full bg-red-500/10 border border-red-500/20 mb-3">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                  </div>
                  <CardTitle className="text-white">Creation Failed</CardTitle>
                  <CardDescription className="text-red-300/70">
                    {error || 'Something went wrong during avatar creation.'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleRetryUpload}
                    className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-lg"
                    id="dt-retry-upload-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
};
